import { computed } from 'vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import { CAMERA_TYPES, DEVICE_TYPES } from '@/Utils/vice-constants';
import { generateCameraData } from '@/Utils/helpers';

function useExternalCameraComposable() {
  const multiCameraDevicesStore = useLocalStreamingDevicesStore();
  const isDeviceSetupChanged = multiCameraDevicesStore.isSessionSetupWasChanged;

  // Set main camera
  const setMainCamera = async () => {
    const cameraName = CAMERA_TYPES.INTERNAL_WEBCAM;
    const predefinedCamLabel = getStoredDeviceProperty(DEVICE_TYPES.MAIN_CAMERA, 'label');
    const predefinedCamConnected = isConnectedByLabel(predefinedCamLabel);

    // Check if the predefined camera exists but is not connected
    multiCameraDevicesStore.checkPredefinedMainCameraConnection();

    if (!isDeviceSetupChanged && predefinedCamConnected) {
      multiCameraDevicesStore.setMainCameraDeviceId(predefinedCamConnected.deviceId);
    }

    // Is INTERNAL camera in the cache and available?
    const cachedMainCamId = multiCameraDevicesStore.cameraTypes[cameraName].cameraDeviceId;
    const cachedMainCamAvailable = isConnectedById(cachedMainCamId);

    if (cachedMainCamId && cachedMainCamAvailable) {
      multiCameraDevicesStore.setMainCameraDeviceId(cachedMainCamId);
    }
  };

  // check if EXTERNAL camera should be added
  const handleExternalCameraConnection = async () => {
    const cameraName = CAMERA_TYPES.EXTERNAL_WEBCAM;
    const predefinedExCamLabel = getStoredDeviceProperty(DEVICE_TYPES.EXTRA_CAMERA, 'label');
    const predefinedExCamConnected = isConnectedByLabel(predefinedExCamLabel);

    // Remove from cache if setup not changed and ex_cam was not predefined
    if (!isDeviceSetupChanged && !predefinedExCamLabel) {
      await multiCameraDevicesStore.removeExternalCameraFromCache();
    }

    // Add to cache if setup not changed and ex_cam predefined
    if (!isDeviceSetupChanged && predefinedExCamConnected) {
      await multiCameraDevicesStore.addExternalCameraToCache(
        predefinedExCamConnected.deviceId,
        predefinedExCamLabel,
      );
    }

    const cachedExCam = multiCameraDevicesStore.connectedCameras.find(
      (camera) => camera.name === CAMERA_TYPES.EXTERNAL_WEBCAM,
    );
    const cachedExCamAvailable = isConnectedById(cachedExCam?.cameraDeviceId);
    if (isCachedExCam.value && cachedExCamAvailable) {
      await addExternalCamera(cameraName, cachedExCam.cameraDeviceId);
    }
  };

  // Set extra camera
  const addExternalCamera = async (cameraName, cameraId) => {
    const isAddedOnInit = true;

    const camData = generateCameraData(
      cameraName,
      multiCameraDevicesStore.allCameraTypes[cameraName]?.cameraDeviceId || cameraId,
      isAddedOnInit,
    );

    await multiCameraDevicesStore.addCameraType(cameraName, {
      [cameraName]: camData,
    });
    // Ensure video track is unmuted for external camera after restoration
    const cameraType = multiCameraDevicesStore.cameraTypes[cameraName];
    if (cameraType?.localTracks) {
      const videoTracks = cameraType.localTracks.filter((track) => track.getType() === 'video');
      if (videoTracks.length > 0) {
        await Promise.all(videoTracks.map((track) => track.unmute()));
        cameraType.isCameraOn = true;
      }
    }
  };

  const isCachedExCam = computed(
    () =>
      !!multiCameraDevicesStore.connectedCameras.some(
        (camera) => camera.name === CAMERA_TYPES.EXTERNAL_WEBCAM,
      ),
  );

  const isConnectedById = (id) =>
    !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
      (device) => device.deviceId === id,
    );

  const isConnectedByLabel = (label) =>
    multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
      (device) => device.label === label,
    );

  // Get stored device ID by device label
  const getStoredDeviceProperty = (deviceLabel, property = 'deviceId') => {
    const storedDevice = multiCameraDevicesStore.clinicianDevicesForSession.clinician_devices.find(
      (device) => device.device_type_label === deviceLabel,
    );

    return storedDevice ? storedDevice.device_data[property] : null;
  };

  return {
    setMainCamera,
    isConnectedById,
    addExternalCamera,
    isConnectedByLabel,
    handleExternalCameraConnection,
  };
}

export default useExternalCameraComposable;
